import { useEffect, useMemo, useState } from "react";

// NS-style platform departure board.
//
// Visual reference: modern Nederlandse Spoorwegen LCD "Vertrek" displays
// found above platforms — dark blue frame, light alternating sky-blue/white
// rows, dark navy text, and four columns: Vertrek | Naar / Opmerkingen |
// Spoor | Trein. Platforms render as little notched ticket tags ("4a",
// "4b") and the train type shows an inline operator/product mark
// (NS swirl for Intercity, the rounded "b" for Sprinter, "DB" for
// Deutsche Bahn services).

type StopOption = {
  id: string;
  name: string;
  code: string;
  lat: number;
  lon: number;
  routes: string[]; // route short names that serve this stop
};

export type RouteGeometry = {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  route_color: string;
  stops: { id: string; name: string; code: string; lat: number; lon: number }[];
};

type Arrival = {
  route_id: string;
  route_short_name: string;
  route_color?: string;
  trip_id: string;
  trip_headsign: string;
  vehicle_id?: string;
  scheduled_arrival?: number;
  predicted_arrival?: number;
  scheduled_departure?: number;
  predicted_departure?: number;
  predicted: boolean;
  status?: string;
  stops_away: number;
};

export type StopArrivals = {
  stop_id: string;
  stop_name: string;
  stop_code?: string;
  arrivals: Arrival[];
  generated: number;
};

const ARRIVAL_REFRESH_MS = 15_000;
const MAX_ROWS = 8;

export function DepartureBoard({
  visible,
  historyTs = null,
}: {
  visible: boolean;
  historyTs?: number | null;
}) {
  const [stops, setStops] = useState<StopOption[]>([]);
  const [routes, setRoutes] = useState<RouteGeometry[]>([]);
  const [stopsErr, setStopsErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [data, setData] = useState<StopArrivals | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const historical = historyTs !== null;

  // Tick once per second so the "min" countdown stays current without
  // re-fetching from the backend. Frozen while viewing history so the board
  // reflects the scrubbed-to instant.
  useEffect(() => {
    if (historical) {
      setNow(historyTs! * 1000);
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [historical, historyTs]);

  // Load route geometry once and derive a flat, deduplicated stop list.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/routes/geometry")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RouteGeometry[]>;
      })
      .then((routes) => {
        if (cancelled) return;
        setRoutes(routes);
        const byId = new Map<string, StopOption>();
        for (const r of routes) {
          for (const s of r.stops ?? []) {
            const existing = byId.get(s.id);
            if (existing) {
              if (!existing.routes.includes(r.route_short_name)) {
                existing.routes.push(r.route_short_name);
              }
              continue;
            }
            byId.set(s.id, {
              id: s.id,
              name: s.name,
              code: s.code,
              lat: s.lat,
              lon: s.lon,
              routes: [r.route_short_name],
            });
          }
        }
        const list = Array.from(byId.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setStops(list);
        // Pick a sensible default station so the board has something to
        // show on first paint.
        if (list.length > 0 && !selectedId) {
          const preferred =
            list.find((s) => /westlake/i.test(s.name)) ??
            list.find((s) => /sea-tac|seatac|airport/i.test(s.name)) ??
            list[0];
          setSelectedId(preferred.id);
        }
      })
      .catch((err) => {
        if (!cancelled) setStopsErr(String(err));
      });
    return () => {
      cancelled = true;
    };
    // selectedId intentionally omitted: we only want to seed the default
    // once, on initial geometry load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the arrivals endpoint for the selected stop. Pause polling while
  // the tab isn't visible so we don't burn OBA quota in the background.
  // When the user has scrubbed back in time, swap in the historical
  // endpoint and debounce so dragging doesn't fire a request per frame.
  useEffect(() => {
    if (!selectedId || !visible) return;
    let cancelled = false;
    let timer: number | null = null;
    let debounce: number | null = null;

    const url = historical
      ? `/api/realtime/arrivals/at?stop_id=${encodeURIComponent(selectedId)}&ts=${encodeURIComponent(String(historyTs))}`
      : `/api/realtime/arrivals?stop_id=${encodeURIComponent(selectedId)}`;

    const load = () => {
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<StopArrivals>;
        })
        .then((d) => {
          if (cancelled) return;
          setData(d);
          setLoadErr(null);
        })
        .catch((err) => {
          if (!cancelled) setLoadErr(String(err));
        });
    };

    if (historical) {
      // Debounce slider drags so we don't hammer the backend.
      debounce = window.setTimeout(load, 120);
    } else {
      load();
      timer = window.setInterval(load, ARRIVAL_REFRESH_MS);
    }
    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
      if (debounce !== null) window.clearTimeout(debounce);
    };
  }, [selectedId, visible, historical, historyTs]);

  const selectedStop = useMemo(
    () => stops.find((s) => s.id === selectedId) ?? null,
    [stops, selectedId],
  );

  const rows = useMemo(
    () => buildRows(data, now, routes, selectedId),
    [data, now, routes, selectedId],
  );

  return (
    <div className="board-wrap">
      <div className="board-controls">
        <label className="board-station-label" htmlFor="board-station">
          Station
        </label>
        <select
          id="board-station"
          className="board-station-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {stops.length === 0 && <option value="">Loading stations…</option>}
          {stops.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.routes.length > 0 ? `  ·  ${s.routes.join(" / ")} Line` : ""}
            </option>
          ))}
        </select>
        {stopsErr && <span className="board-error">{stopsErr}</span>}
      </div>

      <div className="board" role="region" aria-label="Departures display board">
        <BoardHeader
          stop={selectedStop}
          generated={data?.generated ?? 0}
          now={now}
          loadErr={loadErr}
        />

        <div className="board-grid board-grid-head" aria-hidden="true">
          <div className="col-time">Vertrek</div>
          <div className="col-dest">Naar / Opmerkingen</div>
          <div className="col-platform">Spoor</div>
          <div className="col-type">Trein</div>
        </div>

        <div className="board-rows">
          {rows.length === 0 && (
            <div className="board-empty">
              {loadErr
                ? "Geen verbinding met de server"
                : data
                  ? "Geen vertrektijden bekend"
                  : "Vertrektijden laden…"}
            </div>
          )}
          {rows.map((row) => (
            <BoardRow key={row.key} row={row} now={now} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BoardHeader({
  stop,
  now,
}: {
  stop: StopOption | null;
  generated: number;
  now: number;
  loadErr: string | null;
}) {
  return (
    <div className="board-header">
      <div className="board-header-title">
        <span className="board-header-eyebrow">Vertrektijden</span>
        <span className="board-header-station">
          {stop ? stop.name : "—"}
        </span>
      </div>
      <div className="board-header-meta">
        <span className="board-header-clock" aria-label="Huidige tijd">
          <ClockIcon />
          <span className="board-header-clock-h">{formatClockHH(now)}</span>
          <span className="board-header-clock-sep">:</span>
          <span className="board-header-clock-m">{formatClockMM(now)}</span>
        </span>
      </div>
    </div>
  );
}

function ClockIcon() {
  return (
    <svg
      className="ns-clock-icon"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M8 4.2 V8 L10.6 9.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export type Row = {
  key: string;
  // Best-known time (predicted if available, scheduled otherwise) as unix ms.
  whenMs: number;
  // Scheduled time as unix ms; used for the displayed HH:MM.
  scheduledMs: number;
  destination: string;
  via: string;
  routeShort: string;
  routeColor: string;
  delayMin: number;
  predicted: boolean;
  status: string;
  stopsAway: number;
  // Synthesized presentation field, since OBA doesn't tell us the
  // platform letter.
  platform: string;
  platformSub: string; // "a" / "b" suffix shown as a small superscript
};

export function buildRows(
  data: StopArrivals | null,
  nowMs: number,
  routes: RouteGeometry[],
  selectedStopId: string,
): Row[] {
  if (!data) return [];
  const rows: Row[] = [];
  for (const a of data.arrivals) {
    // Prefer departure time at this stop (the board is "vertrek"); fall
    // back to arrival for the terminus.
    const schedSec = a.scheduled_departure || a.scheduled_arrival || 0;
    const predSec = a.predicted_departure || a.predicted_arrival || 0;
    const bestSec = predSec || schedSec;
    if (!bestSec) continue;
    const bestMs = bestSec * 1000;
    // Drop trips that have already left more than a minute ago.
    if (bestMs < nowMs - 60_000) continue;
    const delayMin =
      predSec && schedSec ? Math.round((predSec - schedSec) / 60) : 0;
    const { destination, via } = splitHeadsign(a.trip_headsign);
    // Headsigns from OBA rarely embed an explicit "via X" hint, so fall
    // back to deriving 1-2 intermediate stops from the route geometry
    // between the selected station and the destination.
    const viaText =
      via ||
      deriveVia(routes, a.route_short_name, selectedStopId, destination);
    rows.push({
      key: `${a.trip_id}-${schedSec}`,
      whenMs: bestMs,
      scheduledMs: (schedSec || bestSec) * 1000,
      destination,
      via: viaText,
      routeShort: a.route_short_name || "—",
      routeColor: a.route_color ? `#${a.route_color}` : "#ffcc00",
      delayMin,
      predicted: a.predicted,
      status: a.status || "",
      stopsAway: a.stops_away,
      platform: synthPlatform(a.route_short_name),
      platformSub: synthPlatformSub(a.trip_id),
    });
  }
  rows.sort((a, b) => a.whenMs - b.whenMs);
  return rows.slice(0, MAX_ROWS);
}

// Find 1-2 intermediate stops along the route between the currently selected
// station and the trip's destination. Used to populate the "Via" column when
// the headsign itself doesn't carry a via hint (the typical case for Link).
export function deriveVia(
  routes: RouteGeometry[],
  routeShort: string,
  selectedStopId: string,
  destination: string,
): string {
  if (!routes.length || !routeShort || !selectedStopId || !destination) {
    return "";
  }
  const route = routes.find(
    (r) => r.route_short_name.toLowerCase() === routeShort.toLowerCase(),
  );
  if (!route || !route.stops?.length) return "";
  const fromIdx = route.stops.findIndex((s) => s.id === selectedStopId);
  if (fromIdx < 0) return "";
  const destNorm = normStation(destination);
  // Search for the destination stop on each side; trips on this route can
  // be heading either direction, so the dest may be before or after us.
  let destIdx = -1;
  for (let i = 0; i < route.stops.length; i++) {
    if (i === fromIdx) continue;
    if (normStation(route.stops[i].name) === destNorm) {
      destIdx = i;
      break;
    }
  }
  if (destIdx < 0) {
    // Fall back to a looser substring match in case names differ slightly
    // (e.g. "Sea-Tac/Airport" vs "SeaTac/Airport").
    for (let i = 0; i < route.stops.length; i++) {
      if (i === fromIdx) continue;
      const n = normStation(route.stops[i].name);
      if (n.includes(destNorm) || destNorm.includes(n)) {
        destIdx = i;
        break;
      }
    }
  }
  if (destIdx < 0) return "";
  const step = destIdx > fromIdx ? 1 : -1;
  const between: string[] = [];
  for (let i = fromIdx + step; i !== destIdx; i += step) {
    between.push(route.stops[i].name);
  }
  if (between.length === 0) return "";
  // For short hops show every intermediate stop (max 2). For longer trips
  // pick stops at roughly 1/3 and 2/3 to give a sense of the path without
  // crowding the column.
  if (between.length <= 2) return between.join(", ");
  const a = between[Math.floor(between.length / 3)];
  const b = between[Math.floor((between.length * 2) / 3)];
  return a === b ? a : `${a}, ${b}`;
}

export function normStation(name: string): string {
  return name
    .toLowerCase()
    .replace(/\bstation\b/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

// Some headsigns embed a "via" hint, e.g. "Lynnwood via Capitol Hill".
// Split it out so we can mimic the NS layout, where via stops live in a
// dedicated column under the destination.
export function splitHeadsign(headsign: string): { destination: string; via: string } {
  if (!headsign) return { destination: "—", via: "" };
  const m = headsign.match(/^(.*?)\s+via\s+(.+)$/i);
  if (m) return { destination: m[1].trim(), via: m[2].trim() };
  return { destination: headsign, via: "" };
}

function BoardRow({ row, now }: { row: Row; now: number }) {
  const minsToGo = Math.max(0, Math.round((row.whenMs - now) / 60_000));
  const cancelled = /cancel/i.test(row.status);
  const imminent = minsToGo === 0 && row.whenMs <= now + 30_000;
  // Build the small grey "via …" subline that sits under the destination,
  // matching the NS LCD layout. When delayed/cancelled we promote the
  // remark to a coloured line so it reads as a status, not a route hint.
  const remark = cancelled
    ? "Rijdt niet"
    : row.delayMin > 0
      ? `Ca. ${row.delayMin} min later`
      : "";
  return (
    <div
      className={`board-grid board-row${cancelled ? " cancelled" : ""}${imminent ? " imminent" : ""}`}
    >
      <div className="col-time">
        <span className="time-scheduled">{formatHM(row.scheduledMs)}</span>
      </div>
      <div className="col-dest">
        <span className="dest-name">{row.destination}</span>
        {row.via && <span className="dest-via">via {row.via}</span>}
        {remark && <span className="dest-remark">{remark}</span>}
      </div>
      <div className="col-platform">
        <PlatformTag number={row.platform} sub={row.platformSub} />
      </div>
      <div className="col-type">
        <NsLogo />
        <span className="train-type-label">{lineLabel(row.routeShort)}</span>
      </div>
    </div>
  );
}

// Notched "ticket" tag mimicking the platform indicator on NS LCD boards.
// The number sits left, an optional small superscript letter ("a"/"b")
// distinguishes adjacent platforms sharing one number.
function PlatformTag({ number, sub }: { number: string; sub: string }) {
  return (
    <span className="platform-tag" aria-label={`Spoor ${number}${sub}`}>
      <span className="platform-tag-num">{number}</span>
      {sub && <span className="platform-tag-sub">{sub}</span>}
    </span>
  );
}

// Inline Nederlandse Spoorwegen double-arrow logo, drawn as a single dark-blue
// path with a slit through the middle (rendered via fill-rule even-odd) to
// create the recognisable interlocking N silhouette.
function NsLogo() {
  return (
    <svg
      className="train-mark mark-ns-logo"
      viewBox="0 0 100 40"
      width="34"
      height="14"
      aria-label="NS"
    >
      <path
        fill="#003082"
        fillRule="evenodd"
        d="M0 20 L20 0 L40 0 L31 9 L62 9 L62 0 L100 20 L80 40 L60 40 L69 31 L38 31 L38 40 Z M31 14 L62 14 L62 18 L35 18 Z M38 22 L65 22 L69 26 L38 26 Z"
      />
    </svg>
  );
}

// Format a route short name (e.g. "1 Line", "2") as the Dutch "Lijn N" label.
export function lineLabel(routeShort: string): string {
  const m = (routeShort || "").match(/(\d+)/);
  if (m) return `Lijn ${m[1]}`;
  const trimmed = (routeShort || "").trim();
  return trimmed ? `Lijn ${trimmed}` : "—";
}

// Sound Transit doesn't publish a platform/track number per arrival, so we
// synthesize a stable label from the route. It mimics the NS "spoor"
// column without inventing data we don't have.
export function synthPlatform(routeShort: string): string {
  const m = routeShort.match(/(\d+)/);
  if (m) return m[1];
  return "—";
}

// Half of trips on a shared platform get an "a" sub-letter, the others get
// "b" — purely cosmetic, but it lets the board show the recognisable
// "4ᵃ / 4ᵇ" notched-tag pattern from real NS LCD displays.
export function synthPlatformSub(tripId: string): string {
  if (!tripId) return "";
  let h = 0;
  for (let i = 0; i < tripId.length; i++) h = (h * 31 + tripId.charCodeAt(i)) | 0;
  return (h & 1) === 0 ? "a" : "b";
}

export function formatHM(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function formatClockHH(ms: number): string {
  return String(new Date(ms).getHours()).padStart(2, "0");
}

function formatClockMM(ms: number): string {
  return String(new Date(ms).getMinutes()).padStart(2, "0");
}
